'use client';
import React, { useEffect, useRef } from 'react';

// Start loading p5 IMMEDIATELY when this module is parsed (not waiting for useEffect)
// This reduces animation start delay by ~200-500ms
let p5Promise: Promise<typeof import('p5')> | null = null;
if (typeof window !== 'undefined') {
  p5Promise = import('p5');
}

/**
 * ParticleFlowField - A stunning P5.js particle flow field animation
 *
 * Features:
 * - 1500 particles with glowing trails
 * - Multi-layered Perlin noise for organic flow
 * - Dynamic pentadic color palette (5 colors, cycling)
 * - Mouse interaction (attract particles on press)
 * - Additive blending for cosmic glow effect
 * - Edge wrapping for seamless flow
 */
const RetroTerminal: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const p5InstanceRef = useRef<any>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const resizeHandlerRef = useRef<((width: number, height: number) => void) | null>(null);

  useEffect(() => {
    // Use the already-started promise (or start it now as fallback)
    const loadP5 = async () => {
      const p5Module = await (p5Promise || import('p5'));
      const p5 = p5Module.default;

      // P5.js sketch definition
      const sketch = (p: any) => {
        let particles: any[] = [];
        let zOff = 0; // Will be set to mature value in setup()
        const noiseScale = 0.005;
        let colors: any[] = [];

        // Particle class definition
        class Particle {
          pos: any;
          vel: any;
          acc: any;
          size: number;

          constructor() {
            this.pos = p.createVector(p.random(p.width), p.random(p.height));
            // Start with random velocity for immediate motion
            this.vel = p.createVector(p.random(-2, 2), p.random(-2, 2));
            this.acc = p.createVector(0, 0);
            this.size = p.random(2, 6); // Variable sizes for depth illusion
          }

          update() {
            // Multi-octave Perlin noise for complex, organic flow field
            // Math: Base noise (scale 1) + 0.5 * higher frequency octave (scale 2x) for detail
            // Using 3D noise with zOff for time evolution, creating fluid dynamics
            const x = this.pos.x * noiseScale;
            const y = this.pos.y * noiseScale;
            const noise1 = p.noise(x, y, zOff);
            const noise2 = 0.5 * p.noise(x * 2, y * 2, zOff);
            const n = noise1 + noise2;
            const angle = n * p.TWO_PI; // Map noise (0-1.5) to angle (0-3PI), providing directional variety
            const force = p5.Vector.fromAngle(angle);
            force.mult(0.08); // Reduced force for more independent movement
            this.acc.add(force);

            // Add random jitter for more chaotic movement
            const randomForce = p.createVector(p.random(-0.05, 0.05), p.random(-0.05, 0.05));
            this.acc.add(randomForce);

            // Mouse interaction: Attract particles when pressed (reduced strength)
            if (p.mouseIsPressed) {
              const mouse = p.createVector(p.mouseX, p.mouseY);
              const dir = p5.Vector.sub(mouse, this.pos);
              const dist = dir.mag();
              dir.normalize();
              const strength = 0.01 / (dist + 1); // Reduced from 0.05
              dir.mult(strength);
              this.acc.add(dir);
            }

            // Particle physics: Euler integration for position update
            // Vel limited to maxSpeed for controlled motion
            this.vel.add(this.acc);
            this.vel.limit(3); // maxSpeed = 3
            this.pos.add(this.vel);
            this.acc.mult(0); // Reset acceleration

            this.edges(); // Wrap around edges for infinite space feel
          }

          display() {
            // Random color from dynamic palette for variety
            const colIndex = Math.floor(p.random(6));
            p.fill(colors[colIndex]);
            p.ellipse(this.pos.x, this.pos.y, this.size);
          }

          edges() {
            // Utility: Edge wrapping to keep particles in canvas, enhancing seamless flow
            if (this.pos.x > p.width) this.pos.x = 0;
            if (this.pos.x < 0) this.pos.x = p.width;
            if (this.pos.y > p.height) this.pos.y = 0;
            if (this.pos.y < 0) this.pos.y = p.height;
          }
        }

        p.setup = () => {
          const canvas = p.createCanvas(
            containerRef.current?.clientWidth || window.innerWidth,
            containerRef.current?.clientHeight || window.innerHeight
          );
          canvas.parent(containerRef.current!);

          p.colorMode(p.HSB, 360, 100, 100, 255); // HSB mode for easy hue manipulation
          p.blendMode(p.ADD); // Additive blending for glowing, ethereal effects
          p.noStroke(); // No outlines for soft, fluid appearance

          p.background(0); // Black background

          // Advance the noise field to a mature state WITHOUT simulating particles
          // This skips the expensive 8000 particles × 300 frames = 2.4M updates
          // zOff starts at 100 + (300 frames × 0.002) = 100.6 for mature flow patterns
          zOff = 100.6;

          // Initialize particles - they already have random velocities for immediate motion
          for (let i = 0; i < 8000; i++) {
            particles.push(new Particle());
          }

          // Store resize handler for external access (ResizeObserver)
          resizeHandlerRef.current = (newWidth: number, newHeight: number) => {
            p.resizeCanvas(newWidth, newHeight);
            p.background(0);
            // Reposition particles within new bounds
            for (const particle of particles) {
              if (particle.pos.x > newWidth) particle.pos.x = p.random(newWidth);
              if (particle.pos.y > newHeight) particle.pos.y = p.random(newHeight);
            }
          };
        };

        p.draw = () => {
          // Background fade
          p.blendMode(p.BLEND);
          p.background(0, 0, 0, 20);
          p.blendMode(p.ADD);

          // Dynamic colors: Time-based hue shift combined with custom palette
          // Includes rgb(100, 103, 242) = HSB(239, 59, 95) as anchor color
          const baseHue = (p.frameCount * 0.1) % 360;
          colors = [
            p.color(239, 59, 95, 8),  // rgb(100, 103, 242) - indigo anchor
            p.color(250, 55, 90, 8),  // purple variation
            p.color(220, 50, 100, 8), // soft blue
            p.color((baseHue + 72) % 360, 80, 100, 8),  // dynamic color 1
            p.color((baseHue + 144) % 360, 80, 100, 8), // dynamic color 2
            p.color(320, 60, 95, 8),  // pink accent
          ];

          // Increment zOff for noise evolution, simulating fluid time progression
          zOff += 0.002;

          // Update and display all particles
          for (const particle of particles) {
            particle.update();
            particle.display();
          }
        };

        p.windowResized = () => {
          p.resizeCanvas(
            containerRef.current?.clientWidth || window.innerWidth,
            containerRef.current?.clientHeight || window.innerHeight
          );
          p.background(0);

          // Reposition particles within new bounds
          for (const particle of particles) {
            if (particle.pos.x > p.width) particle.pos.x = p.random(p.width);
            if (particle.pos.y > p.height) particle.pos.y = p.random(p.height);
          }
        };
      };

      // Create P5 instance
      if (containerRef.current && !p5InstanceRef.current) {
        p5InstanceRef.current = new p5(sketch);

        // Set up ResizeObserver to handle container resizing
        resizeObserverRef.current = new ResizeObserver(() => {
          if (resizeHandlerRef.current && containerRef.current) {
            const newWidth = containerRef.current.clientWidth;
            const newHeight = containerRef.current.clientHeight;
            if (newWidth > 0 && newHeight > 0) {
              resizeHandlerRef.current(newWidth, newHeight);
            }
          }
        });
        resizeObserverRef.current.observe(containerRef.current);
      }
    };

    loadP5();

    // Cleanup on unmount
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (p5InstanceRef.current) {
        p5InstanceRef.current.remove();
        p5InstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden"
      style={{
        backgroundColor: '#000000',
      }}
    />
  );
};

export default RetroTerminal;
